import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';

export const TermsConditions = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-navy-dark text-white py-6 border-b border-gray-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="text-white hover:text-teal-400 mb-4 -ml-4"
          >
            <ArrowLeft className="mr-2" size={20} />
            Back to Home
          </Button>
          <h1 className="text-3xl font-bold">Terms and Conditions</h1>
          <p className="text-gray-400 mt-2">Last updated: 15.12.2025</p>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-lg max-w-none">
          
          <h2 className="text-2xl font-bold text-navy-dark mt-8 mb-4">About these terms</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            These Terms and Conditions ("Terms") apply to your use of the Weight Prescriber Network website ("Website").
          </p>
          <p className="text-gray-700 leading-relaxed mb-6">
            By using this Website, you agree to these Terms. If you do not agree, please do not use the Website.
          </p>

          <h2 className="text-2xl font-bold text-navy-dark mt-8 mb-4">Who we are</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Weight Prescriber Network ("we", "us", "our") provides pharmacist-led prescribing capacity services to regulated UK weight management providers (B2B).
          </p>
          <ul className="list-none ml-0 mb-6 text-gray-700">
            <li>Operator: PHARMKG LTD</li>
            <li>Company number: 14702569</li>
            <li>Address: 261 High Street, Waltham Cross, EN8 7BE, United Kingdom</li>
            <li>Email: info@weightprescribernetwork.co.uk</li>
          </ul>

          <h2 className="text-2xl font-bold text-navy-dark mt-8 mb-4">Website purpose and no patient use</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            This Website is intended for business users only.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            It does not provide medical advice and is not a service for patients.
          </p>
          <p className="text-gray-700 leading-relaxed mb-6 font-semibold">
            Do not use the Website to submit patient data or medical information.
          </p>

          <h2 className="text-2xl font-bold text-navy-dark mt-8 mb-4">Enquiries and no binding offer</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Information on this Website is for general information only and does not constitute an offer capable of acceptance.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            Submitting an enquiry does not create a contract.
          </p>
          <p className="text-gray-700 leading-relaxed mb-6">
            Any services are provided subject to a separate written agreement.
          </p>

          <h2 className="text-2xl font-bold text-navy-dark mt-8 mb-4">Acceptable use</h2>
          <p className="text-gray-700 leading-relaxed mb-4">You agree not to:</p>
          <ul className="list-disc ml-6 mb-6 text-gray-700">
            <li>Use the Website in any unlawful way</li>
            <li>Attempt to gain unauthorised access to the Website or its systems</li>
            <li>Introduce viruses, malware, or harmful code</li>
            <li>Submit false, misleading, or defamatory content</li>
            <li>Use the Website to send confidential third-party information (including patient data)</li>
          </ul>
          <p className="text-gray-700 leading-relaxed mb-6">
            We may suspend or restrict access if we believe there has been a breach of these Terms.
          </p>

          <h2 className="text-2xl font-bold text-navy-dark mt-8 mb-4">Intellectual property</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            All content on this Website (including text, graphics, branding, layout, and logos) is owned by or licensed to us.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            You may view and print pages for your internal business use only.
          </p>
          <p className="text-gray-700 leading-relaxed mb-6">
            You must not copy, reproduce, distribute, or create derivative works without our prior written consent.
          </p>

          <h2 className="text-2xl font-bold text-navy-dark mt-8 mb-4">Third-party links</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            The Website may contain links to third-party websites. We do not control or endorse those websites and are not responsible for their content or availability.
          </p>

          <h2 className="text-2xl font-bold text-navy-dark mt-8 mb-4">Availability and changes</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            We may change, suspend, or withdraw the Website (in whole or part) at any time without notice.
          </p>
          <p className="text-gray-700 leading-relaxed mb-6">
            We do not guarantee that the Website will always be available, error-free, or secure.
          </p>

          <h2 className="text-2xl font-bold text-navy-dark mt-8 mb-4">Disclaimer</h2>
          <p className="text-gray-700 leading-relaxed mb-4">To the maximum extent permitted by law:</p>
          <ul className="list-disc ml-6 mb-6 text-gray-700">
            <li>We provide the Website "as is" and "as available"</li>
            <li>We do not warrant that information on the Website is complete, accurate, or up to date at all times</li>
            <li>You are responsible for verifying information before relying on it</li>
          </ul>

          <h2 className="text-2xl font-bold text-navy-dark mt-8 mb-4">Limitation of liability</h2>
          <p className="text-gray-700 leading-relaxed mb-4">Nothing in these Terms excludes or limits liability for:</p>
          <ul className="list-disc ml-6 mb-6 text-gray-700">
            <li>Death or personal injury caused by negligence</li>
            <li>Fraud or fraudulent misrepresentation</li>
            <li>Any liability that cannot be excluded or limited under law</li>
          </ul>
          <p className="text-gray-700 leading-relaxed mb-4">Subject to the above, we are not liable for:</p>
          <ul className="list-disc ml-6 mb-6 text-gray-700">
            <li>Any indirect, consequential, or special loss</li>
            <li>Loss of profits, business, revenue, goodwill, or anticipated savings</li>
            <li>Loss arising from your inability to use the Website or reliance on Website content</li>
          </ul>

          <h2 className="text-2xl font-bold text-navy-dark mt-8 mb-4">Privacy</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            We process personal data in accordance with our Privacy Policy, which forms part of these Terms.
          </p>

          <h2 className="text-2xl font-bold text-navy-dark mt-8 mb-4">Governing law and jurisdiction</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            These Terms are governed by the laws of England and Wales.
          </p>
          <p className="text-gray-700 leading-relaxed mb-6">
            Any disputes will be subject to the exclusive jurisdiction of the courts of England and Wales.
          </p>

          <h2 className="text-2xl font-bold text-navy-dark mt-8 mb-4">Contact</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            If you have questions about these Terms, contact: info@weightprescribernetwork.co.uk
          </p>

        </div>

        <div className="mt-12 pt-8 border-t border-gray-200">
          <Button
            onClick={() => navigate('/')}
            className="bg-teal-700 hover:bg-teal-800 text-white"
          >
            <ArrowLeft className="mr-2" size={18} />
            Return to Home
          </Button>
        </div>
      </div>
    </div>
  );
};
